// Which figures a section of the sales report shows: a button beside the
// section's title and a small dialog of switches. What each reader picks is
// kept in their browser (LocalUserPreferences), per section — not everyone
// wants every figure, and the ones they hide stay hidden next time.
import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Checkbox from "aldoo-ra/Checkbox"
import LocalUserPreferences from "aldoo-ra/LocalUserPreferences"
import { AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

/**
 * The keys a section shows: the reader's pick, else `defaults`. Answers [keys, setKeys].
 * A pick is kept with the keys there were then (`known`), so a figure added later
 * (one of `defaults` the reader never saw) shows for them too, and one they hid
 * stays hidden. A pick saved before `known` was kept is a bare list: `added` names
 * the figures that came after it.
 */
export function useFields(storageKey, defaults, { added = [] } = {}) {
  const [keys, setKeysState] = useState(() => {
    try {
      const saved = LocalUserPreferences.get(storageKey)
      const pick = Array.isArray(saved) ? saved : saved?.keys
      if (!Array.isArray(pick) || !pick.length) return defaults
      const known = Array.isArray(saved?.known) ? saved.known : defaults.filter((key) => !added.includes(key) || pick.includes(key))
      return [...pick, ...defaults.filter((key) => !known.includes(key) && !pick.includes(key))]
    } catch {}
    return defaults
  })
  const setKeys = (next) => {
    setKeysState(next)
    try {
      LocalUserPreferences.set(storageKey, { keys: next, known: defaults })
    } catch {}
  }
  return [keys, setKeys]
}

/**
 * `fields`: [{ key, label }] that may be shown; `value`: the keys shown;
 * `onChange(keys)`; `t`; `defaults`: what "Reset" goes back to. At least one
 * stays on: a section of nothing says nothing. `helpKey`: the help center's
 * words on the dialog, beside its title; `helpKeyOf(key)`: on each field.
 */
export default function FieldsPicker({ fields, value, onChange, defaults, t, title, testId = "sales-fields", helpKey, helpKeyOf }) {
  const [open, setOpen] = useState(false)
  const toggle = (key, on) => {
    const next = on ? [...new Set([...value, key])] : value.filter((k) => k !== key)
    if (!fields.some((field) => next.includes(field.key))) return
    // in the fields' own order, so a re-shown figure takes its usual place
    onChange(fields.map((field) => field.key).filter((k) => next.includes(k)))
  }
  return (
    <>
      <Button variant="admin_secondary" onClick={() => setOpen(true)} title={t`Choose the fields shown`} data-testid={testId}>
        <AdjustmentsHorizontalIcon className="w-4 h-4 mr-1.5" />
        {t`Fields`}
      </Button>
      {open && (
        <Modal
          isOpen
          onClose={() => setOpen(false)}
          variant="admin_compact"
          title={helpKey ? <span className="inline-flex items-center gap-1.5">{title || t`Fields`}<HelpHint helpKey={helpKey} /></span> : title || t`Fields`}
          styleOverrides={{ container: "bg-admin_paper w-full max-w-[360px] max-h-[80vh] overflow-y-auto rounded-md border border-admin_bordered shadow-lg" }}
        >
          <div className="flex flex-col gap-1 px-4 pb-4" data-testid={`${testId}-dialog`}>
            {fields.map((field) => (
              <div key={field.key} className="py-1.5 flex items-center gap-1">
                <Checkbox
                  // a tick re-mounts it, so the box follows the list it belongs to
                  key={`${field.key}-${value.includes(field.key)}`}
                  variant="admin_compact"
                  isOn={value.includes(field.key)}
                  onChange={(on) => toggle(field.key, on)}
                  label={t(field.label)}
                  labelClassName="text-sm ml-2"
                />
                {helpKeyOf && <HelpHint helpKey={helpKeyOf(field.key)} placement="bottom" align="start" />}
              </div>
            ))}
            <div className="flex justify-between gap-2 pt-3 mt-1 border-t border-admin_bordered">
              <Button variant="admin_secondary" onClick={() => onChange(defaults.filter((key) => fields.some((field) => field.key === key)))}>
                {t`Reset`}
              </Button>
              <Button variant="admin_primary" onClick={() => setOpen(false)}>
                {t`Done`}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}
